import React, { useState, useCallback, useEffect } from 'react';
import TenantConfigService from '../../services/TenantConfigService';

/**
 * Calendar Patterns CSV Uploader
 * 
 * Access this page directly at: /admin/upload-calendar-patterns
 * Not included in main navigation - admin utility page only
 * 
 * This creates the calendar pattern structure for each division.
 * Must be run AFTER divisions are created and BEFORE time blocks are uploaded.
 * 
 * Expected CSV format:
 * division_code, pattern_name, description, pattern_type, rotation_days, start_date
 * 
 * Example:
 * EC, Main Schedule, Regular EC Schedule, time_blocks, 10, 2025-08-18
 * B, Main Schedule, Test B Schedule, time_blocks, 10, 2025-08-18
 * LS, Weekly Pattern, Lower School Weekly, weekly, 1, 2025-08-18
 */

const CalendarPatternsUploader = ({ currentUser }) => {
  const [file, setFile] = useState(null);
  const [parsedData, setParsedData] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [previewMode, setPreviewMode] = useState(true);
  const [selectedPatterns, setSelectedPatterns] = useState(new Set());
  const [parseError, setParseError] = useState(null);
  const [divisions, setDivisions] = useState([]);
  const [existingPatterns, setExistingPatterns] = useState({});

  const tenantId = currentUser?.tenantId;

  // Load existing divisions and calendar patterns for reference
  useEffect(() => {
    const loadData = async () => {
      if (!tenantId) return;
      
      try {
        const [divisionsConfig, calendarConfig] = await Promise.all([
          TenantConfigService.getConfig(tenantId, 'divisions', { divisions: [] }),
          TenantConfigService.getConfig(tenantId, 'calendar_assignments', {})
        ]);
        
        setDivisions(divisionsConfig.divisions || []);
        setExistingPatterns(calendarConfig || {});
        
        console.log('📅 Loaded divisions:', divisionsConfig.divisions);
        console.log('📅 Loaded existing calendar patterns:', calendarConfig);
      } catch (err) {
        console.error('Error loading config data:', err);
      }
    };
    
    loadData();
  }, [tenantId]);

  // Generate unique ID
  const generateId = () => `pattern_${Date.now()}${Math.random().toString(36).substr(2, 9)}`;

  // Parse CSV file
  const parseCSV = useCallback((text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) {
      throw new Error('CSV must have a header row and at least one data row');
    }

    // Parse header
    const header = lines[0].split(',').map(h => h.trim().replace(/"/g, '').toLowerCase());
    
    // Find column indices
    const divisionIdx = header.findIndex(h => 
      h === 'division_code' || h === 'division' || h === 'code'
    );
    const nameIdx = header.findIndex(h => 
      h === 'pattern_name' || h === 'name'
    );
    const descriptionIdx = header.findIndex(h => 
      h === 'description' || h === 'desc'
    );
    const patternTypeIdx = header.findIndex(h => 
      h === 'pattern_type' || h === 'type'
    );
    const rotationDaysIdx = header.findIndex(h => 
      h === 'rotation_days' || h === 'rotationdays' || h === 'rotation'
    );
    const startDateIdx = header.findIndex(h => 
      h === 'start_date' || h === 'startdate' || h === 'start'
    );

    if (divisionIdx === -1) {
      throw new Error('CSV must have a "division_code" or "division" column');
    }

    // Parse data rows
    const patterns = [];
    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      if (!line.trim()) continue;

      // Handle quoted values with commas
      const values = [];
      let current = '';
      let inQuotes = false;
      for (const char of line) {
        if (char === '"') {
          inQuotes = !inQuotes;
        } else if (char === ',' && !inQuotes) {
          values.push(current.trim());
          current = '';
        } else {
          current += char;
        }
      }
      values.push(current.trim());

      const divisionCode = values[divisionIdx]?.replace(/"/g, '').trim();
      if (!divisionCode) continue;

      // Get optional fields with defaults
      const patternName = nameIdx !== -1 ? values[nameIdx]?.replace(/"/g, '').trim() : 'Main Schedule';
      const description = descriptionIdx !== -1 ? values[descriptionIdx]?.replace(/"/g, '').trim() : '';
      const patternType = patternTypeIdx !== -1 ? values[patternTypeIdx]?.replace(/"/g, '').trim() : 'time_blocks';
      const rotationDays = rotationDaysIdx !== -1 ? parseInt(values[rotationDaysIdx]?.replace(/"/g, '').trim()) || 10 : 10;
      const startDate = startDateIdx !== -1 ? values[startDateIdx]?.replace(/"/g, '').trim() : '';

      patterns.push({
        _rowId: `${divisionCode}_${i}`,
        divisionCode,
        patternName: patternName || 'Main Schedule',
        description: description || `Schedule pattern for ${divisionCode}`,
        patternType: patternType || 'time_blocks',
        rotationDays,
        startDate: startDate || new Date().toISOString().split('T')[0]
      });
    }

    return patterns;
  }, []);

  // Handle file selection
  const handleFileChange = useCallback((e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setParseError(null);
    setUploadStatus(null);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const patterns = parseCSV(event.target.result);
        console.log('📋 Parsed CSV patterns:', patterns);
        console.log('📋 Divisions found:', [...new Set(patterns.map(p => p.divisionCode))]);
        setParsedData(patterns);
        setSelectedPatterns(new Set(patterns.map(p => p._rowId)));
      } catch (err) {
        setParseError(err.message);
        setParsedData([]);
      }
    };
    reader.readAsText(selectedFile);
  }, [parseCSV]);

  // Toggle pattern selection
  const togglePattern = (rowId) => {
    const newSelected = new Set(selectedPatterns);
    if (newSelected.has(rowId)) {
      newSelected.delete(rowId);
    } else {
      newSelected.add(rowId);
    }
    setSelectedPatterns(newSelected);
  };

  // Select/deselect all
  const toggleAll = () => {
    if (selectedPatterns.size === parsedData.length) {
      setSelectedPatterns(new Set());
    } else {
      setSelectedPatterns(new Set(parsedData.map(p => p._rowId)));
    }
  };

  // Check if division exists
  const divisionExists = (code) => {
    return divisions.some(d => d.code === code);
  };

  // Check if pattern already exists for division
  const patternExists = (divisionCode) => {
    const divConfig = existingPatterns[divisionCode];
    return divConfig && Array.isArray(divConfig.patterns) && divConfig.patterns.length > 0;
  };

  // Upload to Firestore
  const handleUpload = async () => {
    console.log('🚀 handleUpload called!', { tenantId, selectedPatterns: selectedPatterns.size, parsedData: parsedData.length });
    
    if (!tenantId) {
      setUploadStatus({ type: 'error', message: 'No tenant ID found. Please log in again.' });
      return;
    }

    const patternsToUpload = parsedData.filter(p => selectedPatterns.has(p._rowId));
    console.log('📦 Patterns to upload:', patternsToUpload);
    
    if (patternsToUpload.length === 0) {
      setUploadStatus({ type: 'error', message: 'No patterns selected for upload.' });
      return;
    }

    // Check for divisions that don't exist
    const missingDivisions = patternsToUpload
      .filter(p => !divisionExists(p.divisionCode))
      .map(p => p.divisionCode);
    
    if (missingDivisions.length > 0) {
      setUploadStatus({ 
        type: 'error', 
        message: `The following divisions don't exist yet: ${[...new Set(missingDivisions)].join(', ')}. Please create them first using the Divisions Uploader.` 
      });
      return;
    }

    setUploading(true);
    setUploadStatus(null);

    try {
      // Get existing calendar_assignments config
      const existingConfig = await TenantConfigService.getConfig(tenantId, 'calendar_assignments', {});
      console.log('📅 Existing calendar config:', existingConfig);

      // Build updated config
      const updatedConfig = { ...existingConfig };
      
      let totalAdded = 0;
      let totalSkipped = 0;
      
      for (const pattern of patternsToUpload) {
        const { divisionCode, patternName, description, patternType, rotationDays, startDate } = pattern;
        
        console.log(`📅 ========== Processing division: ${divisionCode} ==========`);
        
        // Initialize division if it doesn't exist
        if (!updatedConfig[divisionCode]) {
          console.log(`📅 Creating NEW calendar entry for division: ${divisionCode}`);
          updatedConfig[divisionCode] = {
            patterns: [],
            specificDates: []
          };
        }
        
        // Ensure arrays exist
        if (!Array.isArray(updatedConfig[divisionCode].patterns)) {
          updatedConfig[divisionCode].patterns = [];
        }
        if (!Array.isArray(updatedConfig[divisionCode].specificDates)) {
          updatedConfig[divisionCode].specificDates = [];
        }
        
        // Check if a pattern with this name already exists
        const existingPattern = updatedConfig[divisionCode].patterns.find(
        p => p.name === patternName
        );

        if (existingPattern) {
        console.log(`📅 Pattern "${patternName}" already exists for ${divisionCode}, skipping`);
        totalSkipped++;
        continue;
        }

        // Check if there's an empty-named pattern we can update instead of creating new
        const emptyNamedPattern = updatedConfig[divisionCode].patterns.find(
        p => !p.name || p.name.trim() === ''
        );

        if (emptyNamedPattern) {
        console.log(`📅 Found empty-named pattern for ${divisionCode}, updating it instead of creating new`);
        emptyNamedPattern.name = patternName;
        emptyNamedPattern.description = description;
        emptyNamedPattern.type = patternType;
        emptyNamedPattern.rotationDays = rotationDays;
        if (startDate) emptyNamedPattern.startDate = startDate;
        if (!Array.isArray(emptyNamedPattern.timeBlocks)) {
            emptyNamedPattern.timeBlocks = [];
        }
        totalAdded++;
        continue;
        }

        // Create new pattern
        const newPattern = {
          id: generateId(),
          name: patternName,
          description: description,
          type: patternType,
          rotationDays: rotationDays,
          startDate: startDate,
          scheduleMapping: {},
          timeBlocks: []
        };
        
        console.log(`📅 Adding new pattern for ${divisionCode}:`, newPattern);
        updatedConfig[divisionCode].patterns.push(newPattern);
        totalAdded++;
      }
      
        console.log('📅 Final config to save:', JSON.stringify(updatedConfig, null, 2));
        console.log('📅 Patterns for B specifically:', updatedConfig['B']?.patterns);

      // Save using TenantConfigService
      const success = await TenantConfigService.updateConfig(tenantId, 'calendar_assignments', updatedConfig);
      console.log('📅 Save result:', success);

      if (success) {
        let message = `Successfully created ${totalAdded} calendar pattern(s)!`;
        if (totalSkipped > 0) {
          message += ` (${totalSkipped} already existed and were skipped)`;
        }
        setUploadStatus({
          type: 'success',
          message
        });
        
        // Refresh existing patterns
        const refreshedConfig = await TenantConfigService.getConfig(tenantId, 'calendar_assignments', {});
        setExistingPatterns(refreshedConfig || {});
      } else {
        throw new Error('Update returned false');
      }

    } catch (err) {
      console.error('❌ Upload error:', err);
      setUploadStatus({
        type: 'error',
        message: `Upload failed: ${err.message}`
      });
    } finally {
      setUploading(false);
    }
  };

  // Get unique divisions from parsed data
  const uniqueDivisions = [...new Set(parsedData.map(p => p.divisionCode))];

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📊 Calendar Patterns Uploader</h1>
        <p style={styles.subtitle}>
          Step 3: Create calendar pattern structures for each division (run BEFORE uploading time blocks)
        </p>
      </div>

      {/* Order of Operations */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📋 Upload Order of Operations</h3>
        <div style={styles.orderList}>
          <div style={styles.orderItem}>
            <span style={styles.orderNumber}>1</span>
            <span>Divisions → <code>/admin/upload-divisions</code></span>
            <span style={styles.checkMark}>✓</span>
          </div>
          <div style={styles.orderItem}>
            <span style={styles.orderNumber}>2</span>
            <span>Schedule Types → <code>/admin/upload-schedule-patterns</code></span>
            <span style={styles.checkMark}>✓</span>
          </div>
          <div style={{...styles.orderItem, ...styles.currentStep}}>
            <span style={{...styles.orderNumber, ...styles.currentNumber}}>3</span>
            <span><strong>Calendar Patterns → You are here</strong></span>
            <span style={styles.arrow}>←</span>
          </div>
          <div style={styles.orderItem}>
            <span style={styles.orderNumber}>4</span>
            <span>Time Blocks → <code>/admin/upload-time-blocks</code></span>
          </div>
        </div>
      </div>

      {/* Reference Info */}
      {divisions.length > 0 && (
        <div style={styles.card}>
          <h3 style={styles.cardTitle}>📚 Available Divisions ({divisions.length})</h3>
          <div style={styles.refList}>
            {divisions.map(d => {
              const hasPattern = patternExists(d.code);
              return (
                <span 
                  key={d.code} 
                  style={{
                    ...styles.refBadge,
                    ...(hasPattern ? styles.refBadgeWithPattern : {})
                  }}
                  title={hasPattern ? 'Has calendar pattern' : 'No calendar pattern yet'}
                >
                  {d.code} {hasPattern ? '✓' : ''}
                </span>
              );
            })}
          </div>
          <p style={styles.legendText}>
            <span style={styles.legendItem}><span style={styles.refBadge}>CODE</span> = No pattern</span>
            <span style={styles.legendItem}><span style={{...styles.refBadge, ...styles.refBadgeWithPattern}}>CODE ✓</span> = Has pattern</span>
          </p>
        </div>
      )}

      {/* Instructions */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📋 CSV Format</h3>
        <div style={styles.instructions}>
          <p>Your CSV file should have the following columns:</p>
          <code style={styles.codeBlock}>
division_code, pattern_name, description, pattern_type, rotation_days, start_date
          </code>
          <div style={styles.fieldList}>
            <p><strong>Required:</strong></p>
            <ul>
              <li><code>division_code</code> - Division code (must exist, e.g., "B", "EC", "HS")</li>
            </ul>
            <p><strong>Optional:</strong></p>
            <ul>
              <li><code>pattern_name</code> - Name for the pattern (default: "Main Schedule")</li>
              <li><code>description</code> - Description of the pattern</li>
              <li><code>pattern_type</code> - "time_blocks", "weekly", or "rotating" (default: "time_blocks")</li>
              <li><code>rotation_days</code> - Number of rotation days (default: 10)</li>
              <li><code>start_date</code> - Pattern start date YYYY-MM-DD (default: today)</li>
            </ul>
          </div>
          <p style={{ marginTop: '12px' }}><strong>Example:</strong></p>
          <code style={styles.codeBlock}>
{`division_code,pattern_name,description,pattern_type,rotation_days,start_date
EC,Main Schedule,Early Childhood Schedule,time_blocks,10,2025-08-18
B,Main Schedule,Test B Division Schedule,time_blocks,10,2025-08-18
LS,Main Schedule,Lower School Schedule,time_blocks,10,2025-08-18
MS,Main Schedule,Middle School Schedule,time_blocks,10,2025-08-18
HS,Main Schedule,High School Schedule,time_blocks,10,2025-08-18`}
          </code>
        </div>
      </div>

      {/* File Upload */}
      <div style={styles.card}>
        <h3 style={styles.cardTitle}>📤 Select File</h3>
        <div style={styles.uploadArea}>
          <input
            type="file"
            accept=".csv"
            onChange={handleFileChange}
            style={styles.fileInput}
            id="csv-upload"
          />
          <label htmlFor="csv-upload" style={styles.uploadLabel}>
            {file ? (
              <>
                <span style={styles.fileName}>✅ {file.name}</span>
                <span style={styles.fileSize}>({(file.size / 1024).toFixed(1)} KB)</span>
              </>
            ) : (
              <>
                <span style={styles.uploadIcon}>📁</span>
                <span>Click to select CSV file or drag and drop</span>
              </>
            )}
          </label>
        </div>

        {parseError && (
          <div style={styles.errorMessage}>
            ❌ {parseError}
          </div>
        )}
      </div>

      {/* Preview */}
      {parsedData.length > 0 && (
        <div style={styles.card}>
          <div style={styles.previewHeader}>
            <h3 style={styles.cardTitle}>
              👁️ Preview ({parsedData.length} patterns for {uniqueDivisions.length} divisions)
            </h3>
            <div style={styles.previewActions}>
              <button onClick={toggleAll} style={styles.selectAllBtn}>
                {selectedPatterns.size === parsedData.length ? 'Deselect All' : 'Select All'}
              </button>
              <span style={styles.selectedCount}>
                {selectedPatterns.size} selected
              </span>
            </div>
          </div>

          <div style={styles.tableContainer}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Select</th>
                  <th style={styles.th}>Division</th>
                  <th style={styles.th}>Pattern Name</th>
                  <th style={styles.th}>Description</th>
                  <th style={styles.th}>Type</th>
                  <th style={styles.th}>Start Date</th>
                  <th style={styles.th}>Status</th>
                </tr>
              </thead>
              <tbody>
                {parsedData.slice(0, previewMode ? 20 : parsedData.length).map((pattern) => {
                  const divExists = divisionExists(pattern.divisionCode);
                  const patExists = patternExists(pattern.divisionCode);
                  
                  return (
                    <tr key={pattern._rowId} style={styles.tr}>
                      <td style={styles.td}>
                        <input
                          type="checkbox"
                          checked={selectedPatterns.has(pattern._rowId)}
                          onChange={() => togglePattern(pattern._rowId)}
                          style={styles.checkbox}
                          disabled={!divExists}
                        />
                      </td>
                      <td style={styles.td}>
                        <code style={{
                          ...styles.divisionCode,
                          ...(divExists ? {} : styles.divisionCodeMissing)
                        }}>
                          {pattern.divisionCode}
                        </code>
                        {!divExists && <span style={styles.warningIcon} title="Division doesn't exist">⚠️</span>}
                      </td>
                      <td style={styles.td}>{pattern.patternName}</td>
                      <td style={styles.td}>
                        <span style={styles.descText}>{pattern.description}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.typeBadge}>{pattern.patternType}</span>
                      </td>
                      <td style={styles.td}>
                        <span style={styles.dateText}>{pattern.startDate}</span>
                      </td>
                      <td style={styles.td}>
                        {!divExists ? (
                          <span style={styles.statusError}>Division missing</span>
                        ) : patExists ? (
                          <span style={styles.statusWarning}>Pattern exists</span>
                        ) : (
                          <span style={styles.statusReady}>Ready</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {parsedData.length > 20 && (
            <button
              onClick={() => setPreviewMode(!previewMode)}
              style={styles.showMoreBtn}
            >
              {previewMode ? `Show all ${parsedData.length} patterns` : 'Show less'}
            </button>
          )}
        </div>
      )}

      {/* Upload Button */}
      {parsedData.length > 0 && (
        <div style={styles.uploadSection}>
          <button
            onClick={handleUpload}
            disabled={uploading || selectedPatterns.size === 0}
            style={{
              ...styles.uploadBtn,
              ...(uploading || selectedPatterns.size === 0 ? styles.uploadBtnDisabled : {})
            }}
          >
            {uploading ? (
              <>⏳ Creating Patterns...</>
            ) : (
              <>🚀 Create {selectedPatterns.size} Calendar Pattern(s)</>
            )}
          </button>

          {uploadStatus && (
            <div style={{
              ...styles.statusMessage,
              ...(uploadStatus.type === 'success' ? styles.successMessage : styles.errorMessage)
            }}>
              {uploadStatus.message}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  header: {
    marginBottom: '24px',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#1a202c',
    margin: '0 0 8px 0',
  },
  subtitle: {
    fontSize: '16px',
    color: '#718096',
    margin: 0,
  },
  card: {
    background: '#ffffff',
    borderRadius: '12px',
    padding: '24px',
    marginBottom: '20px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    border: '1px solid #e2e8f0',
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#2d3748',
    margin: '0 0 16px 0',
  },
  orderList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  orderItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '12px 16px',
    background: '#f7fafc',
    borderRadius: '8px',
    fontSize: '14px',
  },
  currentStep: {
    background: '#ebf8ff',
    border: '2px solid #4299e1',
  },
  orderNumber: {
    width: '28px',
    height: '28px',
    borderRadius: '50%',
    background: '#e2e8f0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontWeight: '700',
    fontSize: '14px',
    color: '#4a5568',
  },
  currentNumber: {
    background: '#4299e1',
    color: '#fff',
  },
  checkMark: {
    marginLeft: 'auto',
    color: '#48bb78',
    fontWeight: '700',
  },
  arrow: {
    marginLeft: 'auto',
    color: '#4299e1',
    fontWeight: '700',
    fontSize: '18px',
  },
  refList: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },
  refBadge: {
    background: '#fed7d7',
    color: '#c53030',
    padding: '4px 12px',
    borderRadius: '6px',
    fontSize: '13px',
    fontWeight: '600',
  },
  refBadgeWithPattern: {
    background: '#c6f6d5',
    color: '#276749',
  },
  legendText: {
    marginTop: '12px',
    fontSize: '12px',
    color: '#718096',
    display: 'flex',
    gap: '16px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  instructions: {
    fontSize: '14px',
    color: '#4a5568',
    lineHeight: '1.6',
  },
  fieldList: {
    marginTop: '12px',
    fontSize: '13px',
  },
  codeBlock: {
    display: 'block',
    background: '#f7fafc',
    padding: '12px 16px',
    borderRadius: '6px',
    fontSize: '11px',
    color: '#2d3748',
    overflowX: 'auto',
    border: '1px solid #e2e8f0',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  uploadArea: {
    position: 'relative',
  },
  fileInput: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    opacity: 0,
    cursor: 'pointer',
  },
  uploadLabel: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '40px',
    border: '2px dashed #cbd5e0',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
    color: '#718096',
    fontSize: '14px',
    gap: '8px',
  },
  uploadIcon: {
    fontSize: '32px',
  },
  fileName: {
    color: '#2b6cb0',
    fontWeight: '600',
  },
  fileSize: {
    color: '#a0aec0',
    fontSize: '12px',
  },
  previewHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: '12px',
  },
  previewActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  selectAllBtn: {
    padding: '6px 12px',
    fontSize: '13px',
    background: '#edf2f7',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    cursor: 'pointer',
    color: '#4a5568',
  },
  selectedCount: {
    fontSize: '13px',
    color: '#718096',
  },
  tableContainer: {
    overflowX: 'auto',
    marginTop: '16px',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '2px solid #e2e8f0',
    color: '#4a5568',
    fontWeight: '600',
    whiteSpace: 'nowrap',
  },
  tr: {
    borderBottom: '1px solid #f0f0f0',
  },
  td: {
    padding: '10px 12px',
    verticalAlign: 'middle',
  },
  checkbox: {
    width: '18px',
    height: '18px',
    cursor: 'pointer',
  },
  divisionCode: {
    background: '#e6fffa',
    color: '#234e52',
    padding: '4px 10px',
    borderRadius: '4px',
    fontSize: '12px',
    fontWeight: '600',
  },
  divisionCodeMissing: {
    background: '#fed7d7',
    color: '#c53030',
  },
  warningIcon: {
    marginLeft: '6px',
  },
  descText: {
    fontSize: '12px',
    color: '#718096',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  },
  typeBadge: {
    background: '#e9d8fd',
    color: '#553c9a',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
  },
  dateText: {
    fontSize: '12px',
    color: '#4a5568',
  },
  statusReady: {
    color: '#276749',
    background: '#c6f6d5',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
  },
  statusWarning: {
    color: '#975a16',
    background: '#fefcbf',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
  },
  statusError: {
    color: '#c53030',
    background: '#fed7d7',
    padding: '3px 8px',
    borderRadius: '4px',
    fontSize: '11px',
    fontWeight: '600',
  },
  showMoreBtn: {
    marginTop: '16px',
    padding: '8px 16px',
    background: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: '6px',
    color: '#4a5568',
    cursor: 'pointer',
    fontSize: '13px',
  },
  uploadSection: {
    textAlign: 'center',
    padding: '20px 0',
  },
  uploadBtn: {
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    background: '#4299e1',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  uploadBtnDisabled: {
    background: '#a0aec0',
    cursor: 'not-allowed',
  },
  statusMessage: {
    marginTop: '16px',
    padding: '12px 20px',
    borderRadius: '8px',
    fontSize: '14px',
  },
  successMessage: {
    background: '#f0fff4',
    color: '#276749',
    border: '1px solid #9ae6b4',
  },
  errorMessage: {
    background: '#fff5f5',
    color: '#c53030',
    border: '1px solid #feb2b2',
    marginTop: '12px',
    padding: '12px',
    borderRadius: '6px',
    fontSize: '14px',
  },
};

export default CalendarPatternsUploader;